// hooks/useSaveManager.js
/**
 * useSaveManager — encapsulates all save/configuration state and logic.
 *
 * Saves automatically when team analysis completes. Every unique team
 * composition (by role+employee fingerprint) maps to one saved lineup.
 * Re-analyzing the same team updates the existing record.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Logger } from '../../../utils/logger';
import { apiFetch } from '../../../utils/api';

const logger = new Logger('useSaveManager');

/**
 * @param {object} params
 * @param {object|null}  params.staffingData         — current team/role data
 * @param {object|null}  params.optimizationResult   — from staffingStateManager
 * @param {object|null}  params.hookTeamAnalysisData — live chemistry data (triggers save when it arrives)
 * @param {object|null}  params.teamAnalysisData     — merged analysis data (injected or live)
 * @param {function}     params.onSave               — parent callback after a successful save
 */
export function useSaveManager({
  staffingData,
  optimizationResult,
  hookTeamAnalysisData,
  teamAnalysisData,
  onSave,
}) {
  // ── Save status ──────────────────────────────────────────────────────────
  const [isSaving, setIsSaving]         = useState(false);
  const [saveStatus, setSaveStatus]     = useState(null); // 'saving' | 'saved' | 'error' | null
  const [saveError, setSaveError]       = useState(null);
  const [lastSaveTime, setLastSaveTime] = useState(null);

  // ── Configuration identity ───────────────────────────────────────────────
  const [loadedConfigurationId, setLoadedConfigurationId]     = useState(null);
  const [loadedConfigurationName, setLoadedConfigurationName] = useState(null);

  // ── Pending-save flags ───────────────────────────────────────────────────
  const [analysisSavePending, setAnalysisSavePending] = useState(false);
  const [enrichSavePending, setEnrichSavePending]     = useState(false);

  // ── Ref: always-fresh analysis data for handleSave closure ──────────────
  const teamAnalysisDataRef = useRef(null);
  useEffect(() => {
    teamAnalysisDataRef.current = teamAnalysisData;
  }, [teamAnalysisData]);

  // ── Core save handler ─────────────────────────────────────────────────────
  const handleSave = useCallback(async (isDraft = false) => {
    if (!staffingData || isSaving) {
      logger.debug('⏭️ Skipping save: no staffing data or already saving');
      return;
    }

    const assignedLastNames = staffingData.roles
      .filter(r => r.status === 'assigned' && r.assigned_employee_name)
      .map(r => {
        const parts = r.assigned_employee_name.trim().split(/\s+/);
        return parts[parts.length - 1];
      });

    if (assignedLastNames.length === 0) {
      logger.debug('⏭️ Skipping save: no assigned members');
      return;
    }

    const isComplete = staffingData.progress?.completionPercentage === 100;
    if (!isComplete && !isDraft) {
      logger.debug('⏭️ Skipping auto-save: team not complete (%d%%)', staffingData.progress?.completionPercentage ?? 0);
      return;
    }

    const configName = assignedLastNames.join(' - ');

    const teamFingerprint = staffingData.roles
      .filter(r => r.status === 'assigned' && r.assigned_employee_id)
      .map(r => `${r.id ?? ''}:${r.assigned_employee_id ?? ''}`)
      .sort()
      .join('|');

    setIsSaving(true);
    setSaveStatus('saving');

    try {
      const teamMembers = staffingData.roles
        .filter(role => role.status === 'assigned' && role.assigned_employee_id)
        .map(role => {
          const candidateData = role.candidates?.find(c => c.employee_id === role.assigned_employee_id);
          let hourly_rate = null;
          if (role.assigned_hourly_rate) {
            if (typeof role.assigned_hourly_rate === 'string') {
              const match = role.assigned_hourly_rate.match(/\$?(\d+(?:\.\d+)?)/);
              if (match) hourly_rate = parseFloat(match[1]);
            } else {
              hourly_rate = role.assigned_hourly_rate;
            }
          }
          return {
            employee_id: role.assigned_employee_id,
            employee_name: role.assigned_employee_name,
            role: role.role_name,
            role_id: role.id,
            fit_score: role.fit_score || 8.0,
            assigned_tech_score: role.assigned_tech_score || role.fit_score || null,
            assignment_confidence: 0.9,
            hourly_rate,
            employee_cost_rate: candidateData?.employee_cost_rate || candidateData?.cost_rate || null,
            role_billing_rate: role.hourly_rate || role.billing_rate || null,
            allocated_hours: role.total_hours || role.estimated_hours || null,
            availability_tier: candidateData?.availability?.tier || candidateData?.availability_tier || role.assigned_availability_tier,
            next_available_date: candidateData?.availability?.next_available_date || candidateData?.next_available_date || role.assigned_next_available_date,
            available_date: candidateData?.available_date,
            available_from: candidateData?.available_from,
            availability_reason: candidateData?.availability_reason || role.assigned_availability_reason,
          };
        });

      const optimizationAnalysis = optimizationResult ? {
        overall_score: optimizationResult.overall_score || 0,
        technical_coverage: optimizationResult.components?.technical_coverage?.score || 0,
        experience_balance: optimizationResult.components?.experience_balance || 0,
        collaboration_score: optimizationResult.components?.collaboration_score?.score || 0,
        disc_compatibility: optimizationResult.components?.disc_compatibility?.score || 0,
        strengths_synergy: optimizationResult.components?.strengths_synergy?.score || 0,
        risk_assessment: optimizationResult.components?.risk_assessment?.risk_level || 'medium',
        predicted_success_rate: optimizationResult.success_probability || 0,
        confidence_interval: [0.0, 1.0],
        full_components: optimizationResult.components,
      } : {
        overall_score: 8.0, technical_coverage: 8.0, experience_balance: 7.5,
        collaboration_score: 8.2, disc_compatibility: 7.8, strengths_synergy: 7.6,
        risk_assessment: 'low', predicted_success_rate: 0.85, confidence_interval: [0.0, 1.0],
      };

      const comparisonMetrics = {
        estimated_cost: optimizationResult?.components?.margin_analysis?.total_cost || 0,
        estimated_revenue: optimizationResult?.components?.margin_analysis?.total_revenue || 0,
        margin_percentage: optimizationResult?.components?.margin_analysis?.overall_margin || 0,
        estimated_duration_weeks: optimizationResult?.components?.margin_analysis?.project_duration_weeks || 12,
        default_hours_used: optimizationResult?.components?.margin_analysis?.default_hours_used || false,
        risk_factors: optimizationResult?.components?.risk_assessment?.identified_risks || [],
        strengths: ['Strong technical coverage', 'Good collaboration history'],
        skill_coverage_percentage: optimizationResult?.components?.technical_coverage?.coverage_percentage || 85.0,
        team_chemistry_score: optimizationResult?.components?.collaboration_score?.score || 8.2,
        performance_prediction: (optimizationResult?.success_probability || 0.85) * 10,
      };

      const configPayload = {
        project_id: staffingData.id,
        configuration_name: configName,
        optimization_goal: 'performance_maximization',
        team_fingerprint: teamFingerprint,
        team_members: teamMembers,
        optimization_analysis: optimizationAnalysis,
        comparison_metrics: comparisonMetrics,
        estimated_revenue: optimizationResult?.components?.margin_analysis?.total_revenue || 0,
        estimated_cost: optimizationResult?.components?.margin_analysis?.total_cost || 0,
        margin_percentage: optimizationResult?.components?.margin_analysis?.overall_margin || 0,
        default_hours_used: optimizationResult?.components?.margin_analysis?.default_hours_used || false,
        claude_analysis: optimizationResult?.claude_analysis || '',
        detailed_analysis: optimizationResult?.detailed_analysis || '',
        team_analysis_data: teamAnalysisDataRef.current || null,
        client_introduction: optimizationResult?.client_introduction || null,
        user_notes: '',
        ...(isDraft && { configuration_slot: 0, is_partial_draft: true }),
      };

      const response = await apiFetch('team-optimization/configurations', {
        method: 'POST',
        body: JSON.stringify(configPayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success') {
        logger.debug('✅ Auto-save successful:', data.configuration_id);
        setSaveStatus('saved');
        setLastSaveTime(new Date());
        setLoadedConfigurationId(data.configuration_id);
        setLoadedConfigurationName(configName);
        setTimeout(() => setSaveStatus(null), 3000);
        if (onSave) await onSave(staffingData);
      } else {
        throw new Error(data.message || 'Save failed');
      }
    } catch (err) {
      logger.error('❌ Error saving configuration:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 5000);
    } finally {
      setIsSaving(false);
    }
  }, [staffingData, isSaving, optimizationResult, onSave]);

  // ── Auto-save useEffects ──────────────────────────────────────────────────

  // Triggered when analysis completes
  useEffect(() => {
    if (analysisSavePending && staffingData && !isSaving) {
      logger.debug('🔄 Auto-save triggered by analysis completion');
      setAnalysisSavePending(false);
      handleSave();
    }
  }, [analysisSavePending, staffingData, handleSave, isSaving]);

  // When live chemistry data arrives, schedule save
  useEffect(() => {
    if (hookTeamAnalysisData) {
      setAnalysisSavePending(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable setter
  }, [hookTeamAnalysisData]);

  // Re-save when enriched analysis data (detailed_analysis, client_introduction) arrives
  useEffect(() => {
    if (enrichSavePending && staffingData && !isSaving) {
      setEnrichSavePending(false);
      handleSave();
    }
  }, [enrichSavePending, staffingData, handleSave, isSaving]);

  const handleSaveDraft = useCallback(async () => {
    const assignedCount = staffingData?.roles?.filter(r => r.status === 'assigned').length ?? 0;
    if (assignedCount === 0) return;
    await handleSave(true);
  }, [staffingData, handleSave]);

  return {
    // handlers
    handleSave,
    handleSaveDraft,
    // setters needed by JSX / other hooks
    setEnrichSavePending,
    setLoadedConfigurationId,
    setLoadedConfigurationName,
    // state values
    isSaving,
    saveStatus,
    saveError, setSaveError,
    lastSaveTime,
    loadedConfigurationId,
    loadedConfigurationName,
  };
}
